import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { useToast } from './ui/use-toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const EditDealModal = ({ deal, onClose, onSuccess }) => {
  const { toast } = useToast();
  const isFunded = deal.status === 'funded' || deal.type === 'funded';

  // Derive initial % values from the deal's stored dollar amounts so the user sees
  // the same percent-based inputs they used when originally funding.
  const initialFunded = Number(deal.advanced || deal.funding_amount || 0);
  const initialOrigFeeAmt = Number(deal.origination_fee || 0);
  const initialBrokerAmt = Number(deal.broker_commission || deal.commission_total || 0);
  const initialOrigPct = initialFunded > 0 ? +((initialOrigFeeAmt / initialFunded) * 100).toFixed(2) : 0;
  const initialBrokerPct = initialFunded > 0 ? +((initialBrokerAmt / initialFunded) * 100).toFixed(2) : 0;

  const initialFactor = initialFunded > 0 && Number(deal.payback || 0) > 0
    ? +((Number(deal.payback) / initialFunded)).toFixed(4)
    : 0;
  const initialTermDays = (Number(deal.payment_amount || 0) > 0 && Number(deal.payback || 0) > 0)
    ? Math.round(Number(deal.payback) / Number(deal.payment_amount))
    : (Number(deal.term_length) || '');

  const [formData, setFormData] = useState({
    business: deal.business || '',
    advanced: deal.advanced || '',
    payback: deal.payback || '',
    factor_rate: initialFactor ? initialFactor.toString() : '',
    term_days: initialTermDays ? initialTermDays.toString() : '',
    term: deal.term || '',
    iso: deal.iso || '',
    inhouse: deal.inhouse || '',
    date: deal.funding_date || deal.date || '',
    // Percent-based fee inputs (same UX as Fund Deal modal)
    origination_fee_percent: initialOrigPct.toString(),
    broker_commission_percent: initialBrokerPct.toString(),
    payment_amount: deal.payment_amount || '',
    payment_frequency: deal.payment_frequency || 'daily',
    // Expense fields
    uw_fee: deal.uw_fee || 0,
    legal_fee: deal.legal_fee || 0,
    filing_fee: deal.filing_fee || 0,
    ucc_fee: deal.ucc_fee || 0,
    bank_fee: deal.bank_fee || 0,
  });
  const [calcs, setCalcs] = useState({});
  const [loading, setLoading] = useState(false);

  // One-way auto-calcs (same as Fund Deal modal):
  //   Payback = Funded × Factor Rate
  //   Payment = Payback ÷ Term Days
  // Triggered only when the user edits funded / factor. Editing payback directly does
  // NOT reverse-update factor. Payment is always a pure derivation (read-only field).
  const handleAutoCalc = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      const funded = parseFloat(next.advanced) || 0;
      const factor = parseFloat(next.factor_rate) || 0;
      if ((field === 'advanced' || field === 'factor_rate') && funded > 0 && factor > 0) {
        next.payback = (funded * factor).toFixed(2);
      }
      return next;
    });
  };

  // Single source of truth for Payment Amount: whenever payback or term_days changes,
  // payment_amount = payback ÷ term_days. If either is empty/zero, payment clears.
  useEffect(() => {
    const payback = parseFloat(formData.payback) || 0;
    const term = parseInt(formData.term_days) || 0;
    const computed = (payback > 0 && term > 0) ? (payback / term).toFixed(2) : '';
    setFormData(prev => (prev.payment_amount === computed ? prev : { ...prev, payment_amount: computed }));
  }, [formData.payback, formData.term_days]);

  // Auto-calculations — mirror FundDealModal so edit shows the same numbers.
  useEffect(() => {
    const advanced = parseFloat(formData.advanced) || 0;
    const payback = parseFloat(formData.payback) || 0;
    const origFeePct = parseFloat(formData.origination_fee_percent) || 0;
    const brokerCommPct = parseFloat(formData.broker_commission_percent) || 0;
    const dailyPmt = parseFloat(formData.payment_amount) || 0;

    const origFeeAmount = advanced * (origFeePct / 100);
    const netFundedToMerchant = advanced - origFeeAmount;
    // Broker commission is % of FUNDED, industry convention — matches FundDealModal
    const brokerCommAmount = advanced * (brokerCommPct / 100);

    // Primary gross profit = payback − net wired (we kept the origination fee)
    const grossProfit = payback - netFundedToMerchant;
    // Net profit = gross − broker commission
    const netProfit = grossProfit - brokerCommAmount;

    const factorRate = advanced > 0 ? (payback / advanced).toFixed(4) : '0.0000';
    const feePct = advanced > 0 ? (((advanced - netFundedToMerchant) / advanced) * 100).toFixed(2) : '0.00';
    const termDays = dailyPmt > 0 ? Math.round(payback / dailyPmt) : 0;
    const termWeeks = termDays > 0 ? (termDays / 5).toFixed(1) : '0';

    const uw = parseFloat(formData.uw_fee) || 0;
    const legal = parseFloat(formData.legal_fee) || 0;
    const filing = parseFloat(formData.filing_fee) || 0;
    const ucc = parseFloat(formData.ucc_fee) || 0;
    const bank = parseFloat(formData.bank_fee) || 0;
    const totalExpenses = uw + legal + filing + ucc + bank;

    // dealPnl (primary, spec) = collected − net_wired. Collected isn't editable here,
    // so reuse server value for the live preview.
    const collected = Number(deal.collected || 0);
    const dealPnl = collected - netFundedToMerchant;
    const dealPnlNetOfCosts = dealPnl - brokerCommAmount - totalExpenses;

    setCalcs({
      netFunded: netFundedToMerchant,
      origFeeAmount,
      brokerCommAmount,
      factorRate,
      feePct,
      termDays,
      termWeeks,
      totalExpenses,
      grossProfit,
      netProfit,
      dealPnl,
      dealPnlNetOfCosts,
    });
  }, [formData.advanced, formData.payback, formData.origination_fee_percent, formData.broker_commission_percent, formData.payment_amount,
      formData.uw_fee, formData.legal_fee, formData.filing_fee, formData.ucc_fee, formData.bank_fee, deal.collected]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('mcaToken');
      const originalDate = deal.funding_date || deal.date || '';
      const updatePayload = {
        business: formData.business,
        advanced: parseFloat(formData.advanced) || 0,
        payback: parseFloat(formData.payback) || 0,
        term: formData.term,
        iso: formData.iso,
        inhouse: formData.inhouse,
        date: formData.date,
      };

      if (isFunded) {
        // Save dollar amounts (derived) — same shape backend already expects.
        updatePayload.origination_fee = calcs.origFeeAmount || 0;
        updatePayload.broker_commission = calcs.brokerCommAmount || 0;
        updatePayload.net_funded = calcs.netFunded || 0;
        updatePayload.net_amount = calcs.netFunded || 0;
        updatePayload.payment_amount = parseFloat(formData.payment_amount) || 0;
        updatePayload.payment_frequency = formData.payment_frequency;
        if (formData.term_days) {
          updatePayload.term_length = parseInt(formData.term_days) || 0;
        }
        updatePayload.uw_fee = parseFloat(formData.uw_fee) || 0;
        updatePayload.legal_fee = parseFloat(formData.legal_fee) || 0;
        updatePayload.filing_fee = parseFloat(formData.filing_fee) || 0;
        updatePayload.ucc_fee = parseFloat(formData.ucc_fee) || 0;
        updatePayload.bank_fee = parseFloat(formData.bank_fee) || 0;

        if (formData.payment_frequency === 'weekly') {
          updatePayload.weekly = parseFloat(formData.payment_amount) || 0;
          updatePayload.daily = 0;
        } else {
          updatePayload.daily = parseFloat(formData.payment_amount) || 0;
          updatePayload.weekly = 0;
        }
      }

      await axios.put(`${API_BASE}/deals/${deal.id}`, updatePayload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // If this is a funded deal AND the funding date OR term changed, rebuild the
      // payment schedule from the (possibly new) funding_date and new term length.
      const originalTerm = Number(deal.term_length || 0);
      const newTerm = parseInt(formData.term_days) || 0;
      const dateChanged = isFunded && formData.date && formData.date !== originalDate;
      const termChanged = isFunded && newTerm > 0 && newTerm !== originalTerm;
      if (dateChanged || termChanged) {
        try {
          await axios.put(
            `${API_BASE}/deals/${deal.id}/reschedule`,
            { funding_date: formData.date || originalDate },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast({
            title: 'Schedule updated',
            description: dateChanged && termChanged
              ? `Schedule rebuilt — new date ${formData.date} and ${newTerm} payments`
              : dateChanged
                ? `Payment schedule rebuilt from ${formData.date}`
                : `Payment schedule rebuilt with ${newTerm} payments`
          });
        } catch (err) {
          toast({ title: 'Reschedule failed', description: err.response?.data?.detail || err.response?.data?.error || 'Could not rebuild schedule', variant: 'destructive' });
        }
      }

      // Sync broker commission into deal_commissions so _recalc_deal_pnl picks it up
      // (otherwise the 5-min job zeros out broker commission).
      if (isFunded) {
        try {
          await axios.post(`${API_BASE}/deals/${deal.id}/commissions/sync-broker`,
            { amount: calcs.brokerCommAmount || 0 },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (err) {
          // Non-fatal — deal update already succeeded
          console.warn('Broker commission sync failed:', err?.response?.data || err);
        }
      }

      toast({ title: 'Success', description: 'Deal updated successfully' });
      onSuccess();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to update deal', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const field = (label, key, opts = {}) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <Input
        type={opts.type || 'text'}
        step={opts.step}
        value={formData[key]}
        onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
        readOnly={opts.readOnly}
        className={opts.readOnly ? 'bg-gray-50' : ''}
        data-testid={`edit-deal-${key}`}
        placeholder={opts.placeholder}
      />
      {opts.hint && <p className="text-xs text-gray-500 mt-1">{opts.hint}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Edit Deal #{deal.display_number ?? deal.id}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {field('Business Name *', 'business')}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Funded Amount *</label>
              <Input
                type="number"
                data-testid="edit-deal-advanced"
                value={formData.advanced}
                onChange={(e) => handleAutoCalc('advanced', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Payback Amount *</label>
              <Input
                type="number"
                data-testid="edit-deal-payback"
                value={formData.payback}
                onChange={(e) => handleAutoCalc('payback', e.target.value)}
                required
              />
            </div>
          </div>

          {/* Factor Rate + Term Days — same auto-calc UX as Fund Deal */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Factor Rate *</label>
              <Input
                type="number"
                step="0.01"
                data-testid="edit-deal-factor_rate"
                value={formData.factor_rate}
                onChange={(e) => handleAutoCalc('factor_rate', e.target.value)}
                placeholder="1.45"
              />
              <p className="text-xs text-gray-500 mt-1">Funded × Factor = Payback</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Term Days *</label>
              <Input
                type="number"
                data-testid="edit-deal-term_days"
                value={formData.term_days}
                onChange={(e) => handleAutoCalc('term_days', e.target.value)}
                placeholder="110"
              />
              <p className="text-xs text-gray-500 mt-1">Payback ÷ Term Days = Daily Payment</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {field('Term', 'term')}
            {field('Funding Date *', 'date', { type: 'date', hint: isFunded ? 'Changing this rebuilds the payment schedule (business days, preserving cleared/missed/bounced status).' : undefined })}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {field('ISO', 'iso')}
            {field('Company', 'inhouse')}
          </div>

          {isFunded && (
            <>
              {/* Fees as PERCENTAGES — identical UX to Fund Deal modal */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-3">Fees & Payments</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Origination Fee %</label>
                    <Input
                      type="number"
                      step="0.1"
                      data-testid="edit-deal-origination_fee_percent"
                      value={formData.origination_fee_percent}
                      onChange={(e) => setFormData({ ...formData, origination_fee_percent: e.target.value })}
                      placeholder="10"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Deducted from funded: ${(calcs.origFeeAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">ISO/Broker Commission %</label>
                    <Input
                      type="number"
                      step="0.1"
                      data-testid="edit-deal-broker_commission_percent"
                      value={formData.broker_commission_percent}
                      onChange={(e) => setFormData({ ...formData, broker_commission_percent: e.target.value })}
                      placeholder="10"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.broker_commission_percent || 0}% of funded = ${(calcs.brokerCommAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  {/* Payment Amount is a pure derivation: payback ÷ term days. Read-only. */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Payment Amount ($)</label>
                    <Input
                      type="text"
                      data-testid="edit-deal-payment_amount"
                      value={formData.payment_amount ? `$${formData.payment_amount}` : '—'}
                      readOnly
                      className="bg-gray-50 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Auto: Payback ÷ Term Days. Change those to adjust.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Payment Frequency</label>
                    <select
                      value={formData.payment_frequency}
                      onChange={(e) => setFormData({ ...formData, payment_frequency: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Auto-Calculations summary */}
              <div className="bg-green-50 p-4 rounded border border-green-200">
                <h3 className="text-sm font-semibold text-green-800 mb-2">Auto-Calculated</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Net Funded to Merchant:</span>
                    <span className="ml-2 font-bold text-green-700">${(calcs.netFunded || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Factor Rate:</span>
                    <span className="ml-2 font-bold text-blue-700">{calcs.factorRate}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Fee %:</span>
                    <span className="ml-2 font-bold">{calcs.feePct}%</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Gross Profit (payback − net):</span>
                    <span className="ml-2 font-bold">${(calcs.grossProfit || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Net Profit (after commission):</span>
                    <span className="ml-2 font-bold text-green-600">${(calcs.netProfit || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Calc Term:</span>
                    <span className="ml-2 font-bold">{calcs.termDays} days ({calcs.termWeeks} wks)</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Live P&L (collected − net):</span>
                    <span className={`ml-2 font-bold ${(calcs.dealPnl || 0) >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                      ${(calcs.dealPnl || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Net of costs:</span>
                    <span className={`ml-2 font-bold ${(calcs.dealPnlNetOfCosts || 0) >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                      ${(calcs.dealPnlNetOfCosts || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Expenses */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-3">Expenses</h3>
                <div className="grid grid-cols-3 gap-4">
                  {field('UW Fee ($)', 'uw_fee', { type: 'number', step: '0.01' })}
                  {field('Legal Fee ($)', 'legal_fee', { type: 'number', step: '0.01' })}
                  {field('Filing Fee ($)', 'filing_fee', { type: 'number', step: '0.01' })}
                  {field('UCC Fee ($)', 'ucc_fee', { type: 'number', step: '0.01' })}
                  {field('Bank/Wire Fee ($)', 'bank_fee', { type: 'number', step: '0.01' })}
                  <div className="flex items-end">
                    <div className="bg-gray-100 rounded px-3 py-2 w-full text-center">
                      <span className="text-xs text-gray-500 block">Total Expenses</span>
                      <span className="font-bold text-red-600">${(calcs.totalExpenses || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="flex gap-3 pt-4 border-t">
            <Button type="submit" disabled={loading} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="edit-deal-save">
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={loading}>Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDealModal;
